import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";

import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "./slices/authSlice";

import Footer from "./components/Footer";
import { checkDineInExpiry } from "./utils/checkDineInExpiry";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Topbar from "./components/Topbar";
import Menu from "./Menu";
import LoginModal from "./components/LoginModal";
import RegisterModal from "./components/RegisterModal";
import ForgotPasswordModal from "./components/ForgotPasswordModal";
import SeoManager from "./components/SeoManager";
import ThankYouPage from "./pages/ThankYouPage";
import { SettingsProvider } from "./context/SettingsContext";
import DineInRoute from "./pages/DineInRoute";
import OrderList from "./pages/OrderList";
import ContactUs from "./pages/ContactUs";
import AboutUs from "./pages/AboutUs";
import GiftVoucher from "./pages/GiftVoucher";
import ThankYouGift from "./pages/ThankYouGift";
import MyAccount from "./pages/MyAccount";
import ReservationForm from "./pages/ReservationForm";
import AppInRoute from "./pages/AppInRoute";
import CmsPage from "./pages/CmsPage"; 
import { ModalContext } from "./context/ModalContext";
import { useSettings } from "./context/SettingsContext";
import MaintenancePage from "./pages/MaintenancePage";
// ⭐ GLOBAL MODALS
import {
  GlobalModalProvider,
  GlobalModalContext,
} from "./context/ModalProvider";
import CustomPopupModal from "./components/CustomPopupModal";

/* -------------------------------------------------------------------------- */
/*                        GLOBAL PAGE MODAL MANAGER                            */
/* -------------------------------------------------------------------------- */
function ModalManager() {
  const { pages, activeModal, evaluatePageModal, closeModal } =
    React.useContext(GlobalModalContext);

  const location = useLocation();

  useEffect(() => {
    if (!pages || pages.length === 0) return;

    const currentPath = location.pathname.toLowerCase();

    const currentPage = pages.find((p) => {
      const urlPath =
        "/" + p.page_name.replace(/\s+/g, "-").toLowerCase();
      return urlPath === currentPath;
    });

    if (currentPath === "/") {
      evaluatePageModal(5);
    } else if (currentPage) {
      evaluatePageModal(currentPage.id);
    }
  }, [location.pathname, pages]);

  return <CustomPopupModal modal={activeModal} onClose={closeModal} />;
}

/* -------------------------------------------------------------------------- */
/*           URL QUERY AUTH MODAL HANDLER                                      */
/* -------------------------------------------------------------------------- */
function QueryModalHandler({ openLogin, openRegister, closeAllModals }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const modal = searchParams.get("modal");

    if (!modal) return;

    if (user) {
      closeAllModals();
      navigate(".", { replace: true });
      return;
    }

    if (modal === "login") openLogin();
    if (modal === "register") openRegister();

    // remove param AFTER opening
    navigate(".", { replace: true });
  }, [searchParams, user]);

  return null;
}

/* -------------------------------------------------------------------------- */
/*                                   APP                                      */
/* -------------------------------------------------------------------------- */
function App() {
  const [activeModal, setActiveModal] = useState(null); 
  // "login" | "register" | "forgot" | null
  const { settings, loading } = useSettings(); // ✅ get settings



  const dispatch = useDispatch();

  /* ---------------------------- MODAL HANDLERS ---------------------------- */
  const openLogin = () => setActiveModal("login");
  const openRegister = () => setActiveModal("register");
  const openForgot = () => setActiveModal("forgot");
  const closeAllModals = () => setActiveModal(null);

  /* ---------------------------- DINE-IN EXPIRY ----------------------------- */
  useEffect(() => {
    checkDineInExpiry();
  }, []);

  /* ------------------------ FORGOT PASSWORD EVENT ------------------------- */
  useEffect(() => {
    const handleForgot = () => openForgot();
    window.addEventListener("openForgotPassword", handleForgot);
    return () =>
      window.removeEventListener("openForgotPassword", handleForgot);
  }, []);

  /* ------------------------ USER STATUS CHECK ----------------------------- */
  useEffect(() => {
    const checkUserStatus = async () => {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const token = localStorage.getItem("token");

      if (!storedUser?.id || !token) return;

      try {
        const baseUrl = window.BASE_API_URL || "";
        const API_URL = baseUrl.replace(/\/+$/, "") + "/";

        const { data } = await axios.get(
          `${API_URL}checkuserstatus/${storedUser.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!data.status) {
          dispatch(logout());
        }
      } catch (error) {
        console.error("User status check failed", error);
      }
    };

    checkUserStatus();
    const interval = setInterval(checkUserStatus, 15000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const basename =
    window.location.hostname === "localhost" ? "/" : "/menu-app";


      if (loading) return null; // or loader

  // 🚧 BLOCK ENTIRE APP
  if (settings?.is_maintenance == 1) {
    return <MaintenancePage />;
  }
  return (

      <GlobalModalProvider>
        <ModalContext.Provider
          value={{
            openLogin,
            openRegister,
          }}
        >
          <Router basename={basename}>
            <SeoManager />

            {/* ✅ URL MODAL HANDLER */}
            <QueryModalHandler
              openLogin={openLogin}
              openRegister={openRegister}
              closeAllModals={closeAllModals}
            />

            {/* ✅ CMS MODALS */}
            <ModalManager />

            <Topbar onLogin={openLogin} onRegister={openRegister} />

            <Routes>
              <Route path="/" element={<Menu />} />
                <Route path="/page/:slug" element={<CmsPage />} />
              <Route path="/orders" element={<OrderList />} />
              <Route path="/appdevice" element={<AppInRoute />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/dinein" element={<DineInRoute />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/gift-voucher" element={<GiftVoucher />} />
              <Route path="/my-account" element={<MyAccount />} />
              <Route path="/thank-you-gift" element={<ThankYouGift />} />
              <Route path="/reservation" element={<ReservationForm />} />
            </Routes>

            <Footer />

            {/* ---------------- AUTH MODALS ---------------- */}

            {activeModal === "login" && (
              <LoginModal
                onClose={closeAllModals}
                onSwitchToRegister={openRegister}
                onSwitchToForgot={openForgot}
              />
            )}

            {activeModal === "register" && (
              <RegisterModal
                onClose={closeAllModals}
                onSwitchToLogin={openLogin}
              />
            )}

            {activeModal === "forgot" && (
              <ForgotPasswordModal onClose={closeAllModals} />
            )}

            <ToastContainer  style={{ zIndex: 99999 }} />
          </Router>
        </ModalContext.Provider>
      </GlobalModalProvider>
  
  );
}

export default App;